import React from 'react';
import * as yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import FormInput from 'partial/form/FormInput';
import { HiCheckCircle } from 'react-icons/hi';
import Button from 'partial/components/Button';
import FormDate from 'partial/form/FormDate';
import FormFileDnd from 'partial/form/FormFileDnd';
import FormDatasetOptions from 'core-modules/dataset/components/FormDatasetOptions';
import Form, {
  ErrorLabel,
  ErrorOrDescription,
  setError422,
} from 'partial/form/Form';
import { objectToString } from 'helper';

import {
  useSaveBusinessSole,
  useBusinessSole,
  useFormBusinessSole,
} from '../hooks';
import { transformBusinessSole } from '../transformers';

const initialValues = transformBusinessSole({});

const validationSchema = yup.object().shape({
  first_name: yup.string().required('Required'),
  last_name: yup.string().required('Required'),
  district: yup.string().required('Required'),
  street: yup.string().required('Required'),
  house_no: yup.string().required('Required'),
  building_name: yup.string(),
  unit_no: yup.string(),
  postal_code: yup
    .string()
    .required('Required')
    .min(6, 'At least 6 characters')
    .max(6, 'Maximum Postal Code is at 6 characters')
    .matches(/^[A-Za-z0-9]+$/, 'Invalid format'),
  // home_address: yup.string().required('Required'),
  // home_geoloc: yup.string().required('Required'),
  business_legal_name: yup.string().required('Required'),
  business_registration_number: yup.string().required('Required'),
  business_incorporation_date: yup
    .string()
    .required('Required')
    .test(
      'business_incorporation_date',
      'Date must be current or in the past',
      (value) => {
        return dayjs().isSame(value, 'date') || dayjs().isAfter(value, 'date');
      }
    ),
  owner_id_image_url: yup.array().min(1, 'Required'),
  business_cert_image_url: yup.array().min(1, 'Required'),
  business_local_owner_ic_image_url: yup.array().min(2, 'Minimum of 2 images'),
  bank_name: yup.string().required('Required'),
  bank_account_name: yup.string().required('Required'),
  bank_account_number: yup.string().required('Required'),
});

const handleKeyPress = (e) => {
  if (e.charCode >= 48 && e.charCode <= 57) return;
  e.preventDefault();
};

function BusinessSole({ onNext }) {
  const [isLoading, submit] = useSaveBusinessSole();
  const [validateOnChange, setValidateOnChange] = React.useState(false);
  const [, data] = useBusinessSole();
  const {
    values,
    setFieldValue,
    handleSubmit,
    errors,
    setValues,
    setFieldError: onSetFieldError,
  } = useFormik({
    initialValues: { ...initialValues, ...data },
    validationSchema,
    onSubmit: async (form, { setFieldError }) => {
      const param = {
        ...form,
        owner_id_image_url: objectToString(form?.owner_id_image_url),
        business_cert_image_url: objectToString(form?.business_cert_image_url),
        business_local_owner_ic_image_url: objectToString(
          form?.business_local_owner_ic_image_url
        ),
      };
      submit(param, onNext, (err) => {
        setError422(err, setFieldError);
      });
    },
    validateOnChange,
  });
  useFormBusinessSole(setValues);

  return (
    <Form
      className="space-y-4 max-w-3xl mx-auto"
      noValidate
      autoComplete="off"
      onSubmit={handleSubmit}
      error={errors}
    >
      <div className="space-y-3 mb-5">
        <div className="title-h1 text-center">
          Sole Proprietorship/Partnership - Business Information
        </div>
        <p className="text-center text-base text-gray-800">
          Details about your business like bank details, business documents and
          others
        </p>
      </div>
      <hr />
      <div className="space-y-3">
        <div className="title-h2">Owner&apos;s information</div>
        <div>
          <div className="text-sm font-semibold">Full Legal Name</div>
          <div className="text-sm text-gray-500">
            This is the name that appears in any of your government-issued IDs.
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 relative z-20">
          <div>
            <FormInput
              name="first_name"
              onSetFieldValue={setFieldValue}
              value={values?.first_name}
              error={errors?.first_name}
              label="First Name"
              placeholder="Enter First Name"
            />
            <ErrorLabel name="first_name" />
          </div>
          <div>
            <FormInput
              name="last_name"
              onSetFieldValue={setFieldValue}
              value={values?.last_name}
              error={errors?.last_name}
              label="Last Name"
              placeholder="Enter Last Name"
            />
            <ErrorLabel name="last_name" />
          </div>
        </div>
        <div className="text-sm font-semibold">Home Address</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <FormDatasetOptions
              code="DISTRICT"
              name="district"
              onSetFieldValue={setFieldValue}
              value={values?.district}
              error={errors?.district}
              label="District/Mukim/Village"
            />
            <ErrorLabel name="district" />
          </div>
          <div>
            <FormInput
              name="street"
              onSetFieldValue={setFieldValue}
              value={values?.street}
              error={errors?.street}
              label="Street/Simpang"
              placeholder="Enter Street/ Simpang"
            />
            <ErrorLabel name="street" />
          </div>
          <div>
            <FormInput
              name="house_no"
              onSetFieldValue={setFieldValue}
              value={values?.house_no}
              error={errors?.house_no}
              label="Block/House No."
              placeholder="Enter Block/ House No."
            />
            <ErrorLabel name="house_no" />
          </div>
          <div>
            <FormInput
              name="building_name"
              onSetFieldValue={setFieldValue}
              value={values?.building_name}
              error={errors?.building_name}
              optional
              label="Building Name"
              placeholder="Enter Building Name"
            />
            <ErrorLabel name="building_name" />
          </div>
          <div>
            <FormInput
              name="unit_no"
              onSetFieldValue={setFieldValue}
              value={values?.unit_no}
              error={errors?.unit_no}
              label="Floor/Unit No."
              optional
              placeholder="Enter Floor/ Unit No."
            />
            <ErrorLabel name="unit_no" />
          </div>
          <div>
            <FormInput
              name="postal_code"
              onSetFieldValue={setFieldValue}
              value={values?.postal_code}
              error={errors?.postal_code}
              label="Postal Code"
              placeholder="Enter Postal Code"
            />
            <ErrorLabel name="postal_code" />
          </div>
        </div>
        <div className="h-1 bg-gray-200" />
        <div className="flex flex-col space-y-3">
          <div className="title-h2">Upload ID</div>
          <div className="bg-blue-50 border border-blue-100 rounded-md p-4 space-y-1">
            <div className="text-gray-500 font-semibold">
              Guidelines for uploading IDs
            </div>
            <div className="flex items-center space-x-2">
              <HiCheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
              <p className="text-gray-500">Do not submit expired IDs.</p>
            </div>
            <div className="flex items-center space-x-2">
              <HiCheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
              <p className="text-gray-500">
                Make sure all information is legible. Do not censor, black out
                or redact any data.
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <HiCheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
              <p className="text-gray-500">
                Do not crop the ID. All corners of the ID should be visible
                against the backdrop.
              </p>
            </div>
          </div>
          <div>
            <FormFileDnd
              className="h-[176px]"
              name="owner_id_image_url"
              onSetFieldValue={setFieldValue}
              onSetFieldError={onSetFieldError}
              value={values?.owner_id_image_url}
              error={errors?.owner_id_image_url}
              rightAlign
              label="Valid identification documents"
              description={
                <div className="flex flex-col space-y-4">
                  <p className="text-gray-500">
                    Upload your Brunei Identification Card (front and back).
                  </p>
                  <p className="text-gray-500">
                    Accepted files are .jpg, .png or .pdf with a file size lower
                    than 10MB.
                  </p>
                </div>
              }
              max={10}
            />
            <ErrorLabel name="owner_id_image_url" />
          </div>
        </div>
        <div className="h-1 bg-gray-200" />
        <div className="title-h2">Business Information</div>
        <div>
          <FormInput
            name="business_legal_name"
            onSetFieldValue={setFieldValue}
            value={values?.business_legal_name}
            error={errors?.business_legal_name}
            label="Business Legal Name"
            placeholder="Enter Business Legal Name"
          />
          <ErrorOrDescription
            name="business_legal_name"
            label="We'll verify this with the documents you submit, so please make sure it is the exact, full legal name."
          />
        </div>
        <div>
          <FormInput
            name="business_registration_number"
            onSetFieldValue={setFieldValue}
            value={values?.business_registration_number}
            error={errors?.business_registration_number}
            label="Business Registration Number"
            placeholder="Enter Business Registration Number"
          />
          <ErrorOrDescription
            name="business_registration_number"
            label="This is the company registration number that appears in your Certificate of Incorporation."
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-1">
            <FormDate
              name="business_incorporation_date"
              onSetFieldValue={setFieldValue}
              placeholder="DD/MM/YYYY"
              value={values?.business_incorporation_date}
              error={errors?.business_incorporation_date}
              label="Date of Incorporation"
              maxDate={Date.now()}
            />
            <ErrorLabel name="business_incorporation_date" />
          </div>
        </div>
        <div className="h-1 bg-gray-200" />
        <div className="title-h2">Upload documents</div>
        <div className="grid grid-cols-1 gap-3">
          <div className="col-span-1 bg-blue-50 border border-blue-100 rounded-md p-4 space-y-1">
            <div className="text-gray-500 font-semibold">
              Guidelines for uploading documents
            </div>
            <div className="flex items-center space-x-2">
              <HiCheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
              <p className="text-gray-500">
                The documents must be in clear copies and in color.
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <HiCheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
              <p className="text-gray-500">
                Make sure the documents have complete pages.
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <HiCheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
              <p className="text-gray-500">
                The documents must not be redacted (all information is visible
                and data is legible).
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <HiCheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
              <p className="text-gray-500">
                All corners of the documents are visible against the backdrop.
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <HiCheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
              <p className="text-gray-500">
                Accepted files are .jpg, .png or .pdf with a file size lower
                than 10MB.
              </p>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3">
          <div>
            <FormFileDnd
              className="h-[200px]"
              name="business_cert_image_url"
              onSetFieldValue={setFieldValue}
              onSetFieldError={onSetFieldError}
              value={values?.business_cert_image_url}
              error={errors?.business_cert_image_url}
              label="Business Registration (16 & 17) or Business Registration and Registry of Business Name"
              rightAlign
              max={10}
            />
            <ErrorLabel name="business_cert_image_url" />
          </div>
          <div>
            <FormFileDnd
              className="h-[200px]"
              name="business_local_owner_ic_image_url"
              onSetFieldValue={setFieldValue}
              onSetFieldError={onSetFieldError}
              value={values?.business_local_owner_ic_image_url}
              error={errors?.business_local_owner_ic_image_url}
              imagesOnly
              max={10}
              label="Local's Owner IC"
              description={
                <div className="flex flex-col space-y-4">
                  <p className="text-gray-500">
                    Upload Identity Card (front and back).
                  </p>
                  <p className="text-gray-500">
                    Accepted files are .jpg or .png with a file size lower than
                    10MB.
                  </p>
                </div>
              }
              rightAlign
            />
            <ErrorLabel name="business_local_owner_ic_image_url" />
          </div>
        </div>
        <div className="h-1 bg-gray-200" />
        <div className="title-h2">Bank Details</div>
        <div className="bg-blue-50 border border-blue-100 rounded-md p-4 space-y-1">
          <p className="text-gray-500">
            Bank account should be under the name of the registered business.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <FormDatasetOptions
              code="BANKS"
              name="bank_name"
              onSetFieldValue={setFieldValue}
              value={values?.bank_name}
              error={errors?.bank_name}
              label="Bank Name"
            />
            <ErrorLabel name="bank_name" />
          </div>
          <div className="col-span-1">
            <FormInput
              name="bank_account_name"
              onSetFieldValue={setFieldValue}
              value={values?.bank_account_name}
              error={errors?.bank_account_name}
              label="Account Name"
              placeholder="Enter Account Name"
            />
            <ErrorOrDescription
              name="bank_account_name"
              label="This should be under the name of the registered business."
            />
          </div>
          <div className="col-span-1">
            <FormInput
              name="bank_account_number"
              onSetFieldValue={setFieldValue}
              value={values?.bank_account_number}
              error={errors?.bank_account_number}
              label="Account Number"
              onKeyPress={handleKeyPress}
              placeholder="Enter Account Number"
            />
            <ErrorOrDescription
              name="bank_account_number"
              label="This should be a Brunei dollar savings or current account."
            />
          </div>
        </div>
      </div>
      <div className="mt-3 border-t pt-3 space-x-2 flex justify-end">
        <Button
          primary
          size="lg"
          type="submit"
          disabled={isLoading}
          onClick={() => setValidateOnChange(true)}
        >
          Continue to Statement of Acceptance
        </Button>
      </div>
    </Form>
  );
}

BusinessSole.propTypes = {
  onNext: PropTypes.func.isRequired,
  // onBack: PropTypes.func.isRequired,
};

export default BusinessSole;
